import { useRequest } from 'ahooks';
import { default as classNames, default as classnames } from 'classnames';
import React, { useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react';
import Draggable, { DraggableData, DraggableEvent } from 'react-draggable';
import type { LiteralUnion } from 'type-fest';
import { AbstractTooltipProps } from 'antd/es/tooltip';
import { Button as DButton, Select as DSelect, Popover, Scrollbars } from '@meitu/saas-ui';
import { GradientPicker, SketchPicker } from '@meitu/saas-colors';
import IconFont from '@meitu/xdesign-icon-font';
import { getColorCSSText } from '../brandSDK';
import type { IBrandLinearGradientColorFormula } from '../brandSDK/types';
import { Select } from '../ComUI';
import Button from '../ComUI/button';
import { getConfig, useConfig } from '../configProvider';
import { getLocaleStore, useLocale } from '../i18n';
import { checkIsTeam, checkTeamStatusIsFree, getTeamInfo, useTeamInfoState } from '../teamInfo';
import { isPad } from '../utils/browserKit';
import { ENV_ENUM } from '../utils/env';
import eventBus from '../utils/eventBus';
import { ColorCard, fetchColorCards, getBrandListAndMode, IBrand } from './aspis';
import Empty from './empty';
import './index.less';

const DEFAULT_COLORS = [
    '#FFFFFF',
    '#000000',
    '#EE3F27',
    '#F6B35C',
    '#F17D30',
    '#F8F850',
    '#5DD840',
    '#6BF6F5',
    '#2B7BF7',
    '#8546F5',
];

const DEFAULT_COLORS_INTL = ['#FFFFFF', '#000000', '#D7BB9F', '#DAABAC', '#ACB4D5', '#A7D2CA', '#C3D5AB', '#A5C7C9'];

type FastPresetBtns = {
    isTransparentPreset: boolean;
    node: React.ReactNode;
    isActived: boolean;
}[];

type ColorGroupChildrenProp = (args: {
    PopupTrigger: React.ReactNode;
    FastPresetBtns: FastPresetBtns;
}) => React.ReactNode;

interface ITexture {
    name?: string;
    url: string;
    stretch: string;
    scale: number;
}
export interface ColorGroupProps {
    /** 外部去控制Popover的open状态, 如果传了, 则open状态会受外部控制, 否则会受内部控制 */
    open?: boolean;
    popupTriggerSize?: { width?: number; height?: number };
    /**
     * @deprecated
     * 隐藏团队版品牌色卡选择器 (不是团队版账号时, 强制隐藏, 传参无效)
     * 已废弃, 使用hiddenBrandFn替代
     */
    hiddenBrand?: boolean;
    /** 默认逻辑: 团队身份显示品牌色, 非团队身份隐藏品牌色 */
    hiddenBrandFn?: (params: { isTeam: boolean }) => boolean;
    /** 品牌列表排序规则, 默认为 id,asc */
    brandOrderBy?: LiteralUnion<'id,asc' | 'id,desc', string>;
    /** 如果需要展示品牌库的话, 是否隐藏品牌库icon (默认展示) */
    showIcon?: boolean;
    fastPresetColors?: string[];
    useRGBAInput?: boolean;
    color: string;
    gradientDefaultColor?: string;
    setColor: (color: string, transparent: boolean, ext: { isBrandColor: boolean }) => void;
    setColorAfter?: (color: string, transparent: boolean, ext: { isBrandColor: boolean }) => void;
    presetColors?: { color: string; title: string }[];
    gradientPresetColors?: string[];
    placement?: React.ComponentProps<typeof Popover>['placement'];
    popupTriggerStyle?: React.CSSProperties;
    isGradient?: boolean;
    isTexture?: boolean;
    textureData?: ITexture;
    textureChange?: (texture: ITexture) => void;
    selectTextureImageModal?: (props: any) => JSX.Element;
    hideAlpha?: boolean;
    /** 隐藏无填充颜色 */
    hideEmptyColor?: boolean;
    customPopupTrigger?: React.ReactNode;
    /** 表示多种颜色的ui控制 (表格内会使用, 表格内如果有多个单元格颜色不同, 整体表格的颜色就会表示成多色) */
    showMultColor?: boolean;
    /**
     * antd的dropdown组件的getPopupContainer属性
     * 菜单渲染父节点。默认渲染到 body 上，如果你遇到菜单滚动定位问题，试试修改为滚动的区域，并相对其定位
     */
    getPopupContainer?: AbstractTooltipProps['getPopupContainer'];
    onClick?: () => void;
    children?: ColorGroupChildrenProp;
    disabled?: boolean;
    popupOverlayClassName?: string;
    onClickBrandIcon?: () => void;
    draggable?: boolean;
    onToggleBizTab?: (bizType: 'system' | 'team') => void;
    onOpenChange?: (open: boolean) => void;
    tabLock?: boolean;
    /** 组件直接被销毁时, 是否需要触发onOpenChange回调 */
    shouldCallOnOpenChangeByUnmount?: boolean;
    onBrandBodyMouseDown?: (e: React.MouseEvent<HTMLDivElement>) => void;
    onBrandBodyClick?: (e: React.MouseEvent<HTMLDivElement>) => void;
}

const DEFAULT_COLOR = '#ffffffff';

const defaultChildrenProp: ColorGroupChildrenProp = ({ PopupTrigger }) => PopupTrigger;

const bizTabs = () => {
    const { $t } = getLocaleStore();

    return [
        { name: $t((v) => v.colorGroup.systemColor), value: 'system' },
        { name: $t((v) => v.colorGroup.teamColor), value: 'team' },
    ] as const;
};

const TRIGGER_ICON_SIZE = 16;

type BizType = 'system' | 'team';

export interface ColorGroupRef {
    setInitialBizTabOfOpen: (tab: BizType) => void;
}

export const SelectTextureImageModalEvent = 'SelectTextureImageModalEvent';

const ColorGroupOpenEvent = 'ColorGroupOpenEvent';

export const addColorGroupOpenEventListener = (fn: () => void) => eventBus.on(ColorGroupOpenEvent, fn);

export const removeColorGroupOpenEventListener = (fn: () => void) => eventBus.off(ColorGroupOpenEvent, fn);

interface ITextureProps {
    textureData?: ITexture;
    setDraggableDisable?: (disabled: boolean) => void;
    onChange?: (texture: ITexture) => void;
    imageUpload?: React.ReactNode;
}

export const TextureOptions = () => {
    const { $t } = getLocaleStore();
    return [
        {
            value: 'cover',
            label: $t((v) => v.colorGroup.cover),
        },
        {
            value: 'repeat',
            label: $t((v) => v.colorGroup.repeat),
        },
    ];
};

const TextureList = [
    {
        name: '斑驳纹理',
        url: 'https://xiuxiu-pro.meitudata.com/poster/b0a512cd4394df38c73d7bbc33486ad6.jpg',
        thumb: 'https://xiuxiu-pro.meitudata.com/poster/b0a512cd4394df38c73d7bbc33486ad6.jpg?imageView2/2/w/200/q/80/ignore-error/1/',
    },
    {
        name: '黑色斑驳纹理',
        url: 'https://xiuxiu-pro.meitudata.com/poster/a10b8184f184a7ec66a30b3aa8ab2a14.jpg',
        thumb: 'https://xiuxiu-pro.meitudata.com/poster/a10b8184f184a7ec66a30b3aa8ab2a14.jpg?imageView2/2/w/200/q/80/ignore-error/1/',
    },
    {
        name: '黑色古朴底纹',
        url: 'https://xiuxiu-pro.meitudata.com/poster/5be8e16feade2aa3d8c5324dbd4726e6.jpg',
        thumb: 'https://xiuxiu-pro.meitudata.com/poster/5be8e16feade2aa3d8c5324dbd4726e6.jpg?imageView2/2/w/200/q/80/ignore-error/1/',
    },
    {
        name: '黑色墨水纹理',
        url: 'https://xiuxiu-pro.meitudata.com/poster/6e49d30e72ef5f3a5f3360d1fd392a61.jpg',
        thumb: 'https://xiuxiu-pro.meitudata.com/poster/6e49d30e72ef5f3a5f3360d1fd392a61.jpg?imageView2/2/w/200/q/80/ignore-error/1/',
    },
    {
        name: '红色纹理',
        url: 'https://xiuxiu-pro.meitudata.com/poster/c6f937258091951481a5e48e3967eb0f.jpg',
        thumb: 'https://xiuxiu-pro.meitudata.com/poster/c6f937258091951481a5e48e3967eb0f.jpg?imageView2/2/w/200/q/80/ignore-error/1/',
    },
    {
        name: '金色',
        url: 'https://xiuxiu-pro.meitudata.com/poster/dc23b2e547e4de70d9641687fd00168b.jpg',
        thumb: 'https://xiuxiu-pro.meitudata.com/poster/dc23b2e547e4de70d9641687fd00168b.jpg?imageView2/2/w/200/q/80/ignore-error/1/',
    },
    {
        name: '金色2',
        url: 'https://xiuxiu-pro.meitudata.com/poster/ba5667f06917f0b88d02f2a46c587158.jpg',
        thumb: 'https://xiuxiu-pro.meitudata.com/poster/ba5667f06917f0b88d02f2a46c587158.jpg?imageView2/2/w/200/q/80/ignore-error/1/',
    },
    {
        name: '绿色纹理',
        url: 'https://xiuxiu-pro.meitudata.com/poster/7a94841ad9c763ed597b88fecc9e56f2.jpg',
        thumb: 'https://xiuxiu-pro.meitudata.com/poster/7a94841ad9c763ed597b88fecc9e56f2.jpg?imageView2/2/w/200/q/80/ignore-error/1/',
    },
    {
        name: '玫瑰金',
        url: 'https://xiuxiu-pro.meitudata.com/poster/dd210fe4f118afb652e57ef4a9d17f74.jpg',
        thumb: 'https://xiuxiu-pro.meitudata.com/poster/dd210fe4f118afb652e57ef4a9d17f74.jpg?imageView2/2/w/200/q/80/ignore-error/1/',
    },
    {
        name: '弥散',
        url: 'https://xiuxiu-pro.meitudata.com/poster/7b2a9fc4e2cd479309cb9fe889fbd9d0.jpg',
        thumb: 'https://xiuxiu-pro.meitudata.com/poster/7b2a9fc4e2cd479309cb9fe889fbd9d0.jpg?imageView2/2/w/200/q/80/ignore-error/1/',
    },
    {
        name: '牛皮纸',
        url: 'https://xiuxiu-pro.meitudata.com/poster/c2db7ecc7eb3b1967d34f639eda1b315.jpg',
        thumb: 'https://xiuxiu-pro.meitudata.com/poster/c2db7ecc7eb3b1967d34f639eda1b315.jpg?imageView2/2/w/200/q/80/ignore-error/1/',
    },
    {
        name: '水泥墙面',
        url: 'https://xiuxiu-pro.meitudata.com/poster/dca9bd63d17c2c19fa5f2a3391f08588.jpg',
        thumb: 'https://xiuxiu-pro.meitudata.com/poster/dca9bd63d17c2c19fa5f2a3391f08588.jpg?imageView2/2/w/200/q/80/ignore-error/1/',
    },
    {
        name: '宣纸纹理',
        url: 'https://xiuxiu-pro.meitudata.com/poster/3d8be9a732d4a25d0d1b165337437aad.jpg',
        thumb: 'https://xiuxiu-pro.meitudata.com/poster/3d8be9a732d4a25d0d1b165337437aad.jpg?imageView2/2/w/200/q/80/ignore-error/1/',
    },
    {
        name: '锈',
        url: 'https://xiuxiu-pro.meitudata.com/poster/f533ad935175d3cf139acea80947eddc.jpg',
        thumb: 'https://xiuxiu-pro.meitudata.com/poster/f533ad935175d3cf139acea80947eddc.jpg?imageView2/2/w/200/q/80/ignore-error/1/',
    },
    {
        name: '银',
        url: 'https://xiuxiu-pro.meitudata.com/poster/0141f807754daccda6f3e154b526a01b.jpg',
        thumb: 'https://xiuxiu-pro.meitudata.com/poster/0141f807754daccda6f3e154b526a01b.jpg?imageView2/2/w/200/q/80/ignore-error/1/',
    },
    {
        name: '粉色',
        url: 'https://xiuxiu-pro.meitudata.com/poster/391da21e311b48997c52a05d4c082b4d.jpg',
        thumb: 'https://xiuxiu-pro.meitudata.com/poster/391da21e311b48997c52a05d4c082b4d.jpg?imageView2/2/w/200/q/80/ignore-error/1/',
    },
];

const ignoreDragClass = [
    '.color-gradient-select',
    '.c-color-sketch-picker__menu--input',
    '.c-color-sketch-picker__menu--input',
];

const Texture: React.FC<ITextureProps> = ({ textureData, setDraggableDisable, onChange, imageUpload }) => {
    const { $t } = useLocale();
    if (!textureData) return null;

    return (
        <div className="colorGroup-texture-wrapper">
            <div
                className={classNames('colorGroup-texture-upload', {
                    isPreview: !!textureData?.url,
                })}
            >
                {imageUpload}
            </div>
            <div className="colorGroup-texture-options">
                <div className="colorGroup-texture-options__item">
                    <p className="colorGroup-texture-options__item-title">{$t((v) => v.colorGroup.fillingMode)}</p>
                    <div className="colorGroup-texture-options__item-content">
                        <DSelect
                            className="colorGroup-texture-options__item-select"
                            popupClassName="colorGroup-texture-options__item-select-dropdown"
                            onDropdownVisibleChange={(visible) => {
                                setDraggableDisable?.(visible);
                            }}
                            defaultValue={textureData?.stretch}
                            getPopupContainer={(triggerNode) => {
                                return triggerNode?.parentNode as HTMLElement;
                            }}
                            onChange={(value) => {
                                onChange?.({ ...textureData, stretch: value });
                            }}
                        >
                            {TextureOptions().map((item) => {
                                return (
                                    <DSelect.Option key={item.value} value={item.value}>
                                        {item.label}
                                    </DSelect.Option>
                                );
                            })}
                        </DSelect>
                    </div>
                </div>
            </div>
            <div className="colorGroup-texture-presets">
                <p className="colorGroup-texture-presets-title">{$t((v) => v.colorGroup.recommendedTexture)}</p>

                <div className="colorGroup-texture-presets-list">
                    {TextureList.map((item, index) => {
                        return (
                            <div
                                key={index}
                                className="colorGroup-texture-presets-list__item"
                                style={{ backgroundImage: `url(${item.thumb})` }}
                                onClick={() => {
                                    onChange?.({
                                        ...textureData,
                                        url: item.url,
                                        name: item.name,
                                    });
                                }}
                            ></div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

const ColorGroup = React.forwardRef<ColorGroupRef, ColorGroupProps>(
    (
        {
            open,
            popupTriggerSize,
            hiddenBrand: legacyHiddenBrandProp,
            hiddenBrandFn: hiddenBrandFnProp,
            brandOrderBy: brandOrderByProp,
            showIcon: showBrandIcon = true,
            presetColors,
            gradientPresetColors,
            fastPresetColors = getConfig().isIntl ? DEFAULT_COLORS_INTL : DEFAULT_COLORS,
            color = DEFAULT_COLOR,
            setColor,
            setColorAfter,
            placement,
            popupTriggerStyle,
            isGradient = false,
            isTexture = false,
            textureData,
            textureChange,
            selectTextureImageModal: SelectTextureImageModal,
            hideAlpha = false,
            hideEmptyColor = false,
            customPopupTrigger,
            useRGBAInput = false,
            showMultColor = false,
            getPopupContainer,
            onClick,
            gradientDefaultColor,
            children = defaultChildrenProp,
            disabled,
            popupOverlayClassName,
            draggable = true,
            onToggleBizTab,
            onOpenChange,
            tabLock = false,
            shouldCallOnOpenChangeByUnmount = false,
            onBrandBodyMouseDown,
            onBrandBodyClick,
        },
        ref,
    ) => {
        const { $t } = useLocale();
        const teamInfo = useTeamInfoState();
        const isTeam = checkIsTeam(teamInfo);
        const { isIntl } = useConfig();
        const [brandList, setBrandList] = useState<IBrand[] | null>(null);
        const brandEntryModeRef = useRef<string | null>(null);
        const [bizTab, setBizTab] = useState<BizType>('system');
        const [selectedBrandId, setSelectedBrandId] = useState<number | null>(null);
        const [brandColorCards, setBrandColorCards] = useState<ColorCard[]>([]);
        const displayBrandColorCards = useMemo(() => {
            const colorFilter = (c: string | IBrandLinearGradientColorFormula) =>
                isGradient ? true : typeof c === 'string';
            if (isIntl) {
                return [
                    {
                        id: -1,
                        name: '',
                        colors: brandColorCards
                            .map((card) => card.colors)
                            .flat()
                            .filter(colorFilter),
                    },
                ];
            }
            return brandColorCards
                .map<ColorCard>((card) => ({
                    ...card,
                    colors: card.colors.filter(colorFilter),
                }))
                .filter((card) => card.colors.length > 0);
        }, [brandColorCards, isGradient, isIntl]);
        // 刚弹出弹窗时, 显示哪个tab
        const initialBizTabOfOpenRef = useRef<BizType>('system');
        const lastOnOpenChangeStateRef = useRef(false);

        const [currentColor, setCurrentColor] = useState('');
        const isTransparent = useRef(false);
        const [bounds, setBounds] = useState({
            left: 0,
            top: 0,
            bottom: 0,
            right: 0,
        });
        const draggleRef = useRef<HTMLDivElement>(null);
        const [draggableDisable, setDraggableDisable] = useState(false);
        const [texture, setTexture] = useState<ITexture | undefined>(textureData);
        const popoverRef = useRef<any>(null);

        const getIsShowBrand = () => {
            // 兼容历史遗留参数
            const legacyHiddenBrandByConfig = getConfig().colorGroupCommonConfig.hiddenBrand;
            if (legacyHiddenBrandProp !== undefined) {
                console.warn('hiddenBrand prop is deprecated, use hiddenBrandFn instead');
            }
            if (legacyHiddenBrandByConfig !== undefined) {
                console.warn(
                    'colorGroupCommonConfig.hiddenBrand is deprecated, use colorGroupCommonConfig.hiddenBrandFn instead',
                );
            }
            if (
                (legacyHiddenBrandProp !== undefined || legacyHiddenBrandByConfig !== undefined) &&
                (hiddenBrandFnProp ?? getConfig().colorGroupCommonConfig.hiddenBrandFn) &&
                getConfig().env !== ENV_ENUM.dev
            ) {
                throw new Error(
                    'hiddenBrand prop and colorGroupCommonConfig.hiddenBrandFn are both set, please use only one of them',
                );
            }
            if (legacyHiddenBrandProp !== undefined || legacyHiddenBrandByConfig !== undefined) {
                return !(legacyHiddenBrandProp ?? getConfig().colorGroupCommonConfig.hiddenBrand) && isTeam;
            }

            const defaultHiddenBrandFn = ({ isTeam }: { isTeam: boolean }) => !isTeam;
            const hiddenBrandFn =
                hiddenBrandFnProp ?? getConfig().colorGroupCommonConfig.hiddenBrandFn ?? defaultHiddenBrandFn;
            return !hiddenBrandFn({ isTeam });
        };
        const isShowBrand = getIsShowBrand();
        const brandOrderBy = brandOrderByProp ?? getConfig().colorGroupCommonConfig.brandOrderBy ?? 'id,asc';

        useEffect(() => {
            return () => {
                if (shouldCallOnOpenChangeByUnmount && lastOnOpenChangeStateRef.current) {
                    onOpenChange?.(false);
                }
            };
        }, []);

        const onStart = (_event: DraggableEvent, uiData: DraggableData) => {
            const { clientWidth, clientHeight } = window.document.documentElement;
            const targetRect = draggleRef.current?.getBoundingClientRect();
            if (!targetRect) {
                return;
            }
            setBounds({
                left: -targetRect.left + uiData.x,
                right: clientWidth - (targetRect.right - uiData.x),
                top: -targetRect.top + uiData.y,
                bottom: clientHeight - (targetRect.bottom - uiData.y),
            });
        };
        const [popOpen, setPopOpen] = useState(false);

        const refreshTeamInfo = () => {
            if (!isShowBrand) return;

            getBrandListAndMode({
                orderBy: brandOrderBy,
            }).then((res) => {
                if (!res) return null;
                setBrandList(res.brandList);
                brandEntryModeRef.current = res.brandEntryMode;
            });
        };

        useEffect(() => {
            if (!onToggleBizTab) {
                getConfig().colorGroupCommonConfig.ifNoOnToggleBizTabProp?.();
            }
        }, []);

        useEffect(() => {
            if (popOpen) {
                onToggleBizTab?.(bizTab);
            }
        }, [bizTab, popOpen]);

        useEffect(() => {
            if (selectedBrandId === null && brandList?.length) {
                setSelectedBrandId(brandList[0].id ?? null);
            }
        }, [brandList, bizTab]);

        useImperativeHandle(
            ref,
            () => ({
                setInitialBizTabOfOpen: (tab) => (initialBizTabOfOpenRef.current = tab),
            }),
            [],
        );

        const fetchColorCardsCtrl = useRequest(fetchColorCards, { manual: true });

        useEffect(() => {
            const selectedBrand = brandList?.find((item) => item.id === selectedBrandId);
            if (!selectedBrand) return;
            if (!brandEntryModeRef.current) return;

            fetchColorCardsCtrl
                .runAsync(`${selectedBrand.currentNode}/${selectedBrand.id}`, brandEntryModeRef.current)
                .then(setBrandColorCards);
            return fetchColorCardsCtrl.cancel;
        }, [selectedBrandId]);

        useEffect(() => {
            const defaultColor = color || DEFAULT_COLOR;
            isTransparent.current = color ? color.length === 9 && /00$/gi.test(color) : true;
            setCurrentColor(defaultColor);
        }, [color]);

        const handleTextureChange = (data: any) => {
            setTexture(data);
            textureChange?.(data);
        };

        const handleChangeSystemColor = (data: any) => {
            if (data.type === 'texture') {
                handleTextureChange(texture);
                return;
            }
            setCurrentColor(data.color);
            isTransparent.current = data.transparent;
            setColor(data.color, data.transparent, { isBrandColor: false });
        };

        useEffect(() => {
            if (popOpen) {
                setTexture(textureData);
            }
        }, [popOpen]);

        const imageUpload = () => {
            if (!SelectTextureImageModal) return null;
            const addMaterialCallback = (data: any) => {
                handleTextureChange({ ...texture, url: data.url ?? data });
            };
            return (
                <SelectTextureImageModal addMaterialCallback={addMaterialCallback}>
                    {textureData?.url ? (
                        <div
                            className="colorGroup-texture-uploadPreview"
                            style={{ backgroundImage: `url(${textureData?.url})` }}
                        >
                            <DButton
                                type="primary"
                                icon={<IconFont name="RedrawBold" fontSize={16} />}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    eventBus.emit(SelectTextureImageModalEvent, true);
                                }}
                                shape="round"
                            >
                                {$t((v) => v.colorGroup.replaceImage)}
                            </DButton>
                        </div>
                    ) : (
                        <DButton
                            type="primary"
                            onClick={(e) => {
                                e.stopPropagation();
                                eventBus.emit(SelectTextureImageModalEvent, true);
                            }}
                            icon={<IconFont name="UploadBold" />}
                            shape="round"
                        >
                            {$t((v) => v.colorGroup.uploadImage)}
                        </DButton>
                    )}
                </SelectTextureImageModal>
            );
        };

        const SystemBody = isGradient ? (
            <GradientPicker
                isTexture={isTexture}
                useRGBAInput={useRGBAInput}
                textureContent={
                    isTexture ? (
                        <Texture
                            textureData={texture}
                            setDraggableDisable={setDraggableDisable}
                            onChange={handleTextureChange}
                            imageUpload={imageUpload()}
                        />
                    ) : null
                }
                gradientDefaultColor={gradientDefaultColor}
                textureData={texture}
                color={currentColor}
                transparent={isTransparent.current}
                onChange={handleChangeSystemColor}
                onChangeComplete={(data: any) => {
                    setColorAfter?.(data.color, data.transparent, {
                        isBrandColor: false,
                    });
                }}
                tabLock={tabLock}
                gradientPresetColors={gradientPresetColors}
            />
        ) : (
            <SketchPicker
                useRGBAInput={useRGBAInput}
                color={currentColor}
                transparent={isTransparent.current}
                onChange={handleChangeSystemColor}
                onChangeComplete={(data: { color: string; transparent: boolean }) => {
                    setColorAfter?.(data.color, data.transparent, {
                        isBrandColor: false,
                    });
                }}
                hideAlpha={hideAlpha}
                hideEmptyColor={hideEmptyColor}
                presetColors={presetColors}
            />
        );

        /** 打开团队首次购买会员/续费场景 */
        const openVipTeamNormalModal = () =>
            getConfig().colorGroupCommonConfig.openVipModal?.({
                entrance: 'func',
                second_source: 'brand',
                purchase_scene: 'normal',
                oid: getTeamInfo()?.identity.oid,
                groupId: getTeamInfo()?.identity.groupId,
            });

        const renderBrandBody = () => {
            const isEmpty = isIntl
                ? displayBrandColorCards.map((card) => card.colors).flat().length === 0
                : !displayBrandColorCards.length;
            return (
                <div
                    className="colorGroup-brandBody"
                    onMouseDown={(e) => {
                        onBrandBodyMouseDown?.(e);
                    }}
                    onClick={(e) => {
                        onBrandBodyClick?.(e);
                    }}
                >
                    <div className="colorGroup-brandSelect">
                        <Select
                            popupClassName="colorGroup-brandSelectPopup"
                            value={selectedBrandId ?? undefined}
                            options={(brandList ?? []).map(({ id, name }) => ({
                                label: <span className="colorGroup-brandSelect-label">{name}</span>,
                                value: id,
                            }))}
                            onChange={(id) => setSelectedBrandId(id)}
                        />
                    </div>
                    <div className="colorCardList">
                        <Scrollbars>
                            <div style={{ padding: '0 16px' }}>
                                {isEmpty ? (
                                    <Empty
                                        style={{ height: 300 }}
                                        text={
                                            checkTeamStatusIsFree(teamInfo) ? (
                                                <Button
                                                    style={{ width: 92 }}
                                                    type="primary"
                                                    size="small"
                                                    icon={<IconFont name="VipBoldFill" fontSize={14} />}
                                                    onClick={openVipTeamNormalModal}
                                                >
                                                    {$t((v) => v.colorGroup.upgradeTeamEdition)}
                                                </Button>
                                            ) : undefined
                                        }
                                    />
                                ) : (
                                    displayBrandColorCards.map((colorCard) => (
                                        <div key={colorCard.id} className="colorCard">
                                            {!isIntl && <div className="colorCard-header">{colorCard.name}</div>}
                                            <ul className="colorCard-colors">
                                                {colorCard.colors.map((color) => (
                                                    /* eslint-disable-next-line react/jsx-key */
                                                    <li
                                                        style={{ background: getColorCSSText(color) }}
                                                        onClick={() => {
                                                            if (checkTeamStatusIsFree(teamInfo)) {
                                                                openVipTeamNormalModal();
                                                                return;
                                                            }
                                                            const newColor =
                                                                typeof color === 'string'
                                                                    ? color.padEnd(9, 'f')
                                                                    : getColorCSSText(color);
                                                            isTransparent.current = false;
                                                            setCurrentColor(newColor);
                                                            setColor(newColor, false, { isBrandColor: true });
                                                            setColorAfter?.(newColor, false, {
                                                                isBrandColor: true,
                                                            });
                                                        }}
                                                    ></li>
                                                ))}
                                            </ul>
                                        </div>
                                    ))
                                )}
                            </div>
                        </Scrollbars>
                    </div>
                </div>
            );
        };

        const getBrandPopupContentHeight = () => {
            const BIZ_TAB_HEIGHT = 42;
            const bodyHeight = isGradient ? 478 : 433;
            return isShowBrand ? bodyHeight + BIZ_TAB_HEIGHT : bodyHeight;
        };

        // 只能有一个展开的 colorGroup
        useEffect(() => {
            const closeCallback = () => {
                popoverRef.current?.close();
            };

            addColorGroupOpenEventListener(closeCallback);

            return () => {
                removeColorGroupOpenEventListener(closeCallback);
            };
        }, []);

        const PopupContent = (
            <Draggable
                cancel={`${isPad() ? '.widgets-colorPickerBox,' : ''}input,textarea`}
                enableUserSelectHack={false}
                disabled={!draggable || draggableDisable}
                bounds={bounds}
                onStart={(event, uiData) => {
                    const hasIncludeDom = ignoreDragClass.find(
                        (d) =>
                            Array.from(document.querySelectorAll(d)).filter((d) =>
                                d?.contains(event.target as HTMLElement | null),
                            ).length > 0,
                    );
                    return hasIncludeDom ? false : onStart(event, uiData);
                }}
                onMouseDown={(e) => {
                    e.stopPropagation();
                }}
            >
                <div
                    ref={draggleRef}
                    className={classNames('widgets-colorPickerBox', {
                        showBrand: isShowBrand,
                    })}
                    style={
                        isShowBrand && bizTab === 'team'
                            ? { height: getBrandPopupContentHeight(), overflow: 'hidden' }
                            : {}
                    }
                >
                    {isShowBrand && (
                        <ul className="widgets-colorGroup-bizTabs">
                            {bizTabs().map((tab) => (
                                <li
                                    key={tab.value}
                                    data-active={bizTab === tab.value}
                                    onClick={() => setBizTab(tab.value)}
                                >
                                    {tab.name}
                                </li>
                            ))}
                        </ul>
                    )}
                    {bizTab === 'system' ? SystemBody : renderBrandBody()}
                </div>
            </Draggable>
        );

        const PopupTrigger = (
            <div
                className="widgets-color-picker"
                data-is-custom={!!customPopupTrigger}
                data-disabled={disabled}
                style={{ ...popupTriggerStyle, ...popupTriggerSize }}
            >
                <Popover
                    open={open}
                    destroyTooltipOnHide={draggable}
                    overlayClassName={classNames(
                        'widgets-color-picker-Popover',
                        { 'widgets-color-is-gradient-model': isGradient },
                        { 'widgets-color-is-color-model': !isGradient },
                        popupOverlayClassName,
                        draggable ? 'draggable' : '',
                    )}
                    content={PopupContent}
                    trigger="click"
                    ref={popoverRef}
                    placement={placement ?? 'rightBottom'}
                    getPopupContainer={getPopupContainer}
                    onOpenChange={(open) => {
                        lastOnOpenChangeStateRef.current = open;
                        setPopOpen(open);
                        onOpenChange?.(open);
                        if (!open) return;
                        setBizTab(initialBizTabOfOpenRef.current ?? 'system');
                        setSelectedBrandId(null);
                        refreshTeamInfo();
                        eventBus.emit(ColorGroupOpenEvent);
                    }}
                >
                    {customPopupTrigger ?? (
                        <>
                            <div
                                className={classnames('widgets-color-btn', {
                                    'no-icon': !showBrandIcon,
                                })}
                                onClick={() => {
                                    initialBizTabOfOpenRef.current = 'system';
                                    onClick?.();
                                }}
                            >
                                <div className="widgets-colorGroup-color-wrap">
                                    {/* 颜色 */}
                                    <div
                                        className="widgets-colorGroup-color"
                                        style={{
                                            backgroundRepeat: 'no-repeat',
                                            background: textureData?.url
                                                ? `url(${textureData.url}) top /cover`
                                                : showMultColor
                                                  ? 'conic-gradient(from 180deg at 50% 50%, #FF5976 0deg, #F7DD7F 110.62499642372131deg, #60D9F3 191.25deg, #A18AFC 288.7499928474426deg, #FF5A7C 360deg)'
                                                  : currentColor || '#fff',
                                        }}
                                    />
                                    <div className="widgets-mosaic-bg"></div>
                                </div>
                            </div>

                            {isShowBrand && showBrandIcon && (
                                <div
                                    style={{
                                        marginLeft: 6,
                                        marginRight: 2,
                                        cursor: 'pointer',
                                    }}
                                    onClick={() => {
                                        initialBizTabOfOpenRef.current = 'team';
                                    }}
                                >
                                    <IconFont name="RhombusBold" fontSize={TRIGGER_ICON_SIZE - 2} />
                                </div>
                            )}
                        </>
                    )}
                </Popover>
            </div>
        );

        const FastPresetBtns: FastPresetBtns = [
            {
                isTransparentPreset: true,
                node: (
                    <div
                        className={classnames('widgets-fast-select-color-item', {
                            active: isTransparent.current,
                        })}
                        onClick={() => {
                            const newColor = '#ffffff00';
                            isTransparent.current = true;
                            setCurrentColor(newColor);
                            setColor(newColor, true, { isBrandColor: false });
                            setColorAfter?.(newColor, true, { isBrandColor: false });
                        }}
                    >
                        <div className="widgets-mosaic-bg"></div>
                    </div>
                ),
                isActived: isTransparent.current,
            },
            ...fastPresetColors.map((item) => {
                const isActived =
                    !isTransparent.current &&
                    item.padEnd(9, 'f').toLocaleLowerCase() === (currentColor || '').padEnd(9, 'f').toLocaleLowerCase();
                return {
                    isTransparentPreset: false,
                    node: (
                        <div
                            style={{ backgroundColor: item }}
                            className={classnames('widgets-fast-select-color-item', {
                                active: isActived,
                            })}
                            onClick={() => {
                                const newColor = item.padEnd(9, 'f');
                                isTransparent.current = false;
                                setCurrentColor(newColor);
                                setColor(newColor, false, { isBrandColor: false });
                                setColorAfter?.(newColor, false, { isBrandColor: false });
                            }}
                        ></div>
                    ),
                    isActived,
                };
            }),
        ];

        return <>{children({ PopupTrigger, FastPresetBtns })}</>;
    },
);

export default ColorGroup;
